import React, { useState, useEffect } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import InvoiceListLayer from "../components/InvoiceListLayer";
import { API_URL, LOCATION_ID } from "..";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";

const InvoiceListPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalInvoicesCount: 0,
    totalInvoiceAmountCount: 0,
    paidInvoiceCount: 0,
    paidInvoiceAmountCount: 0,
    overdueInvoiceCount: 0,
    overdueInvoiceAmountCount: 0,
    halfPaidInvoicesCount: 0,
  });

  // Default dates → start of month to today
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (date) => date.toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(formatDate(firstDay));
  const [toDate, setToDate] = useState(formatDate(today));

  const fetchDashboardSummary = async (from, to) => {
    let currentUser = JSON.parse(localStorage.getItem("user"));
    let url;

    if (currentUser?.roles?.role === "admin") {
      url = `${API_URL}/api/invoice/dashboard?fromDate=${from}&toDate=${to}&locationId=${LOCATION_ID}`;
    } else {
      url = `${API_URL}/api/invoice/dashboard/user/${currentUser?.id}?fromDate=${from}&toDate=${to}&locationId=${LOCATION_ID}`;
    }

    const api = await fetch(url, { method: "GET" });
    const json = await api.json();
    if (api.status === 200) {
      setSummary(
        json?.summary || {
          totalInvoicesCount: 0,
          totalInvoiceAmountCount: 0,
          paidInvoiceCount: 0,
          paidInvoiceAmountCount: 0,
          overdueInvoiceCount: 0,
          overdueInvoiceAmountCount: 0,
          halfPaidInvoicesCount: 0,
        }
      );
    }
  };

  const fetchInvoices = async (from, to) => {
    setLoading(true);
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      window.location.href = "/sign-in";
    } else {
      let headersList = {
        Accept: "*/*",
        "Content-Type": "application/json",
      };
      let url;
      if (user?.roles?.role === "admin") {
        url = `${API_URL}/api/invoice/get/invoice?fromDate=${from}&toDate=${to}&locationId=${LOCATION_ID}`;
      } else {
        url = `${API_URL}/api/invoice/get/user/invoice/${user?.id}?fromDate=${from}&toDate=${to}&locationId=${LOCATION_ID}`;
      }
      
      let response = await fetch(url, {
        method: "GET",
        headers: headersList,
      });
      const json = await response.json();
      setData(json?.invoices || json || []); // handle {invoices:[]} or []
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchInvoices(fromDate, toDate);
    fetchDashboardSummary(fromDate, toDate);
  }, []); // load once

  const handleFilter = () => {
    fetchInvoices(fromDate, toDate);
    fetchDashboardSummary(fromDate, toDate);
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Invoices" />

      {/* Date Filter */}
      <div className="d-flex align-items-end gap-3 mb-4">
        <div>
          <label className="form-label fw-medium">From</label>
          <input
            type="date"
            className="form-control"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div>
          <label className="form-label fw-medium">To</label>
          <input
            type="date"
            className="form-control"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <div>
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleFilter}
          >
            Filter
          </button>
        </div>
      </div>

      {/* Dashboard Cards */}
      <div className="mt-8 mb-20">
        <div className="col-xxl-12">
          <div className="row gy-4">
            {/* Total Invoices */}
            <Link className="col-xxl-3 col-sm-3" to={`/invoices/all?fromDate=${fromDate}&toDate=${toDate}&locationId=${LOCATION_ID}`}>
              <div>
                <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 ">
                  <div className="card-body p-0">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                      <div className="d-flex align-items-center gap-2">
                        <span className="mb-0 w-48-px h-48-px bg-primary-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6 mb-0">
                          <Icon
                            icon="mingcute:user-follow-fill"
                            className="icon"
                          />
                        </span>
                        <div>
                          <span className="mb-2 fw-medium text-secondary-light text-sm">
                            Total Invoices
                          </span>
                          <h6 className="fw-semibold">
                            {summary.totalInvoicesCount}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Paid Invoices */}
            <Link className="col-xxl-3 col-sm-3" to={`/invoices/paid?fromDate=${fromDate}&toDate=${toDate}&locationId=${LOCATION_ID}`}>
              <div>
                <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 ">
                  <div className="card-body p-0">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                      <div className="d-flex align-items-center gap-2">
                        <span className="mb-0 w-48-px h-48-px bg-success-main flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6">
                          <Icon
                            icon="mingcute:user-follow-fill"
                            className="icon"
                          />
                        </span>
                        <div>
                          <span className="mb-2 fw-medium text-secondary-light text-sm">
                            Paid Invoices
                          </span>
                          <h6 className="fw-semibold">
                            {summary.paidInvoiceCount}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Partially Paid */}
            <Link className="col-xxl-3 col-sm-3" to={`/invoices/partially-paid?fromDate=${fromDate}&toDate=${toDate}&locationId=${LOCATION_ID}`}>
              <div>
                <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 ">
                  <div className="card-body p-0">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                      <div className="d-flex align-items-center gap-2">
                        <span className="mb-0 w-48-px h-48-px bg-success-main flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6">
                          <Icon
                            icon="mingcute:user-follow-fill"
                            className="icon"
                          />
                        </span>
                        <div>
                          <span className="mb-2 fw-medium text-secondary-light text-sm">
                            Partially Paid Invoices
                          </span>
                          <h6 className="fw-semibold">
                            {summary.halfPaidInvoicesCount || 0}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Overdue */}
            <Link className="col-xxl-3 col-sm-3" to={`/invoices/overdue?fromDate=${fromDate}&toDate=${toDate}&locationId=${LOCATION_ID}`}>
              <div>
                <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 ">
                  <div className="card-body p-0">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 mb-8">
                      <div className="d-flex align-items-center gap-2">
                        <span className="mb-0 w-48-px h-48-px bg-danger-main flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6">
                          <Icon
                            icon="mingcute:user-follow-fill"
                            className="icon"
                          />
                        </span>
                        <div>
                          <span className="mb-2 fw-medium text-secondary-light text-sm">
                            Overdue Invoice
                          </span>
                          <h6 className="fw-semibold">
                            {summary.overdueInvoiceCount}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center mt-4">
          <Spinner />
        </div>
      )}

      {!loading && data.length === 0 && <p>No Invoices Found....</p>}

      {!loading && data.length > 0 && <InvoiceListLayer jobsData={data} />}
    </MasterLayout>
  );
};

export default InvoiceListPage;
